import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  signal,
  inject,
  viewChildren,
  output,
  computed,
  ViewEncapsulation,
  linkedSignal,
  untracked,
  effect,
  ElementRef,
  ViewChild,
  viewChild,
  DOCUMENT,
  model,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrSidePanel, type ClrSelectedState } from '@clr/angular';
import { Workbook, CellValue, Worksheet } from 'exceljs';
import { ToastService } from 'src/app/services/toast.service';
import { firstValueFrom, Subject } from 'rxjs';
import shajs from 'sha.js';

import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { SpinnerService } from 'src/app/services/Spinner/spinner.service';
import { DetailviewComponent } from '../../detailview/detailview.component';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { WebSocketService } from 'src/app/services/Websocket/websocket.service';
import { Row, Table } from 'src/app/models/ui/table';
import {
  FILTER_TABLE_STORE,
  FILTER_TABLE_STORE_II,
  provideFilterStore,
} from 'src/app/shared/stores/filter-table.store';
import { ColumnFilterComponent } from '../../filter/column-filter/column-filter.component';
import { CheckInArrayPipe } from 'src/app/pipes/array-methods.pipe';
import { User } from 'src/app/models/api/User';
import { GruppenZuordnungComponent } from './gruppen-zuordnung/gruppen-zuordnung.component';
import { FormGroupOfType } from 'src/app/types/shared';
import { IconDgCellComponent } from '../../_shared/datagrid-cell/icon-dg-cell.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { ConfirmService } from 'src/app/services/confirm.service';
import { Base64Service } from 'src/app/services/base64/base64.service';
import { createExcel_SpezifikationListe, exportTableInExcel } from './export-excel';
import { ACTION_TITLE, ACTION_TREE, ActionMode } from './action-mode';
import { environment } from 'src/environments/environment';
import { ProgressEvent, ProgressGroupStatus, ProgressStatus } from 'src/app/types/progress';
import { AllSelectedBtnComponent } from '../../_shared/all-selected-btn/all-selected-btn.component';
import { SpezPointType } from '../spezifikationsdialog/spezifikationsdialog.component';
import { errorHandle } from 'src/app/misc/functions';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-systemmanagementdialog',
  templateUrl: './systemmanagementdialog.component.html',
  styleUrls: ['./systemmanagementdialog.component.scss'],
  host: {
    '(window:beforeunload)': 'ngOnDestroy()',
  },
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    DetailviewComponent,
    ColumnFilterComponent,
    GruppenZuordnungComponent,
    IconDgCellComponent,
    AllSelectedBtnComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    CheckInArrayPipe,
  ],
  providers: [WebSocketService, provideFilterStore({ secondStore: true })],
  encapsulation: ViewEncapsulation.None,
})
export class SystemManagementComponent {
  private readonly webSocket = inject(WebSocketService);
  private readonly apiService = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastService);
  private readonly confirm = inject(ConfirmService);
  private readonly reader = inject(Base64Service);
  private readonly doc = inject(DOCUMENT);
  private readonly authservice = inject(AuthService);
  protected readonly spinner = inject(SpinnerService);
  protected readonly localeDate = inject(BackendLocaleDatePipe);
  protected readonly filterStore = inject(FILTER_TABLE_STORE);
  protected readonly filterStore_II = inject(FILTER_TABLE_STORE_II);

  private readonly columnFilters = viewChildren<ColumnFilterComponent>('left');
  private readonly columnFilters_II = viewChildren<ColumnFilterComponent>('right');
  private readonly navPanel = viewChild(ClrSidePanel);

  public closedialog = new Subject<boolean>();
  public readonly modalClose = output<boolean>();

  protected readonly progressGroup = signal<ProgressGroupStatus>({ status: 'idle' });

  protected readonly action = signal<ActionMode | null>(null);
  protected readonly tblLeft = signal<Table>(undefined);
  protected readonly tblRight = signal<Table>(undefined);

  protected readonly copyBezeichnung = signal('');
  protected readonly bemerkung = signal('');

  protected readonly trackByKey = computed<string>(() => {
    switch (this.action()) {
      case 'inspRestore':
      case 'inspFullDelete':
        return 'OPRUEFID';
      default:
        return 'OSTAMMID';
    }
  });

  protected readonly selectedtabellenL = linkedSignal<Row>(() => (this.action(), null));
  protected readonly selectedtabellenR = linkedSignal<Row>(() => (this.action(), null));
  protected readonly selectedtabellenLM = linkedSignal<Row[]>(() => (this.action(), []));

  protected readonly isNavPanelOpen = signal(true);
  protected readonly isLoading = signal(false);
  protected readonly isDBBackup = signal(false);
  protected readonly isMultiSelect = computed(() => ['masterDelete', 'masterDeleteMultiple'].includes(this.action()));
  protected readonly isDetailViewLoading = toSignal(this.spinner.active);

  protected readonly sotypListe = signal<ObjektTypModel[]>([]);

  protected readonly titleMap = ACTION_TITLE;
  protected readonly actionsTree = ACTION_TREE;

  protected readonly trTitel = computed(() => ACTION_TITLE[this.action()]);
  protected readonly showInfo = computed(() => this.action() != 'railshift');

  info: string = '';
  info1: string = '';
  pcnt: string = '';
  pcnt1: string = '';
  taskRun: boolean = false;
  rN: number = 0;
  showdetailview = false;
  hidedetailview = false;
  split50 = false;
  _ostammid = 0;
  sidenavenlarged = false;
  sxmrepDelete: boolean = false;
  devDatabase: 'Dev-Datenbank' | 'Testdatenbank';
  logDates: any[];
  logDate: string;
  protected readonly pw = signal<string>(null);

  protected readonly cutGleisForm = new FormGroup({
    cutL: new FormGroupOfType<CutGleisModel>({
      Startposition: new FormControl(null),
      /** TrennPos, Validators dynamisch: Min+Max+Requiered */
      Endposition: new FormControl(null, { updateOn: 'blur' }),
      Startpunkt: new FormControl(null),
      Endpunkt: new FormControl(null),
      Bezeichnung: new FormControl(null),
      Gleislaenge: new FormControl(null),
      Messfile: new FormControl(null),
    }),
    cutR: new FormGroupOfType<CutGleisModel>({
      /** NeuStartPos, Validators dynamisch: Min+Max+Requiered */
      Startposition: new FormControl(null, { updateOn: 'blur' }),
      Endposition: new FormControl(null),
      /** NeuStartPunkt -> NeuEndPunkt in cutL */
      Startpunkt: new FormControl(null, { updateOn: 'blur', validators: Validators.required }),
      Endpunkt: new FormControl(null),
      Bezeichnung: new FormControl(null, Validators.required),
      Gleislaenge: new FormControl(null),
      Messfile: new FormControl(null, Validators.required),
    }),
  });

  protected readonly moveGleisForm = new FormGroup({
    startNeu: new FormControl<number>(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0)] }),
    endNeu: new FormControl<number>(null, Validators.min(0) /* readonly */),
    offset: new FormControl<number>(null, Validators.required /* readonly */),
    toDrehen: new FormControl<boolean>(false, { nonNullable: true }),
  });

  dataRefresh = new EventEmitter();

  showCreateTestDatabase: boolean = false;
  users: Table<User>;
  selectedUsers: User[] = [];
  text: string = '';
  doCreateTestDatabase: boolean = false;

  set data(_: unknown) {}

  constructor() {
    this.checkActionsTree();
    this.webSocket._config.name = 'Sys Action Progress';
    this.webSocket._config.stopOnDestroy = false;

    this.filterStore.registerFilters(this.columnFilters);
    this.filterStore_II.registerFilters(this.columnFilters_II);
    this.filterStore.registerTable(this.tblLeft);
    this.filterStore_II.registerTable(this.tblRight);

    //#region CutGleisForm
    this.cutGleisForm.setValidators((form: typeof this.cutGleisForm) => {
      if (form.invalid) return null;

      if (form.value.cutR.Startposition < form.value.cutL.Endposition) {
        form.markAsTouched();
        return { 'start-vor-end': true };
      }

      return null;
    });
    this.cutGleisForm.get('cutL.Endposition').valueChanges.subscribe(endmeterL => {
      if (endmeterL) {
        const länge = endmeterL - this.cutGleisForm.value.cutL.Startposition;
        this.cutGleisForm.get('cutL.Gleislaenge').setValue(länge);

        const startmeterR = this.cutGleisForm.get('cutR.Startposition');
        if (!startmeterR.value) startmeterR.setValue(endmeterL);
      }
    });
    this.cutGleisForm.get('cutR.Startpunkt').valueChanges.subscribe(startpunktR => {
      if (startpunktR) {
        const endpunktL = this.cutGleisForm.get('cutL.Endpunkt');
        if (!endpunktL.value) endpunktL.setValue(startpunktR);
      }
    });
    this.cutGleisForm.get('cutR.Startposition').valueChanges.subscribe(startmeterR => {
      if (startmeterR) {
        const länge = this.cutGleisForm.value.cutR.Endposition - startmeterR;
        this.cutGleisForm.get('cutR.Gleislaenge').setValue(länge);
      }
    });
    //#endregion
    //#region MoveGleisForm
    this.moveGleisForm
      .get('toDrehen')
      .valueChanges.subscribe(toDrehen => recalcMoveGleis({ toDrehen, startNeu: this.moveGleisForm.value.startNeu }));
    this.moveGleisForm
      .get('startNeu')
      .valueChanges.subscribe(startNeu => recalcMoveGleis({ startNeu, toDrehen: this.moveGleisForm.value.toDrehen }));
    const recalcMoveGleis = ({ toDrehen, startNeu }: { toDrehen: boolean; startNeu: number }) => {
      let offset: number;
      let endNeu: number;

      if (this.moveGleisForm.get('startNeu').valid) {
        const { startmeter, endmeter } = this.selectedtabellenL() as { startmeter: number; endmeter: number };

        if (!toDrehen) {
          offset = startNeu - startmeter;
          endNeu = endmeter + offset;
        } else {
          offset = startNeu - endmeter;
          endNeu = startmeter + offset;
        }
        offset = +offset.toFixed(3);
        endNeu = +endNeu.toFixed(3);
      }

      this.moveGleisForm.get('endNeu').setValue(endNeu);
      this.moveGleisForm.get('endNeu').markAsTouched();
      this.moveGleisForm.get('offset').setValue(offset);
    };
    //#endregion
    effect(() => {
      const selected = this.selectedtabellenL();
      if (selected)
        untracked(() => {
          if (this.action() == 'lineCut') {
            const { startmeter, endmeter, startpunkt, endpunkt, bezeichnung, Messfile } = selected as {
              startmeter: number;
              endmeter: number;
              startpunkt: string;
              endpunkt: string;
              bezeichnung: string;
              Messfile: string;
            };

            this.cutGleisForm.enable({ emitEvent: false });

            const minMaxReq = [
              Validators.required,
              Validators.min(Math.min(startmeter, endmeter)),
              Validators.max(Math.max(startmeter, endmeter)),
            ];
            this.cutGleisForm.get('cutL.Endposition').setValidators(minMaxReq);
            this.cutGleisForm.get('cutR.Startposition').setValidators(minMaxReq);

            this.cutGleisForm.reset({
              cutL: {
                Startposition: startmeter,
                Startpunkt: startpunkt,
                Bezeichnung: bezeichnung,
                Messfile,
              },
              cutR: {
                Endposition: endmeter,
                Endpunkt: endpunkt,
              },
            });
          }

          if (this.action() == 'moveStart') {
            this.moveGleisForm.enable({ emitEvent: false });
            this.moveGleisForm.reset();
          }
          if (this.action() == 'masterArchive') {
            this.showdetailview = true;
            this._ostammid = +selected.OSTAMMID;
          }
        });
      else if (this.action() == 'lineCut') {
        this.cutGleisForm.disable();
        this.cutGleisForm.reset();
      } else if (this.action() == 'moveStart') {
        this.moveGleisForm.disable();
        this.moveGleisForm.reset();
      }
    });
  }

  private checkActionsTree() {
    const db = localStorage.getItem('DB');
    const dbActions = this.actionsTree.find(group => group.groupTitle == 'Datenbank').actions;
    const isDev = db.endsWith('_dev');
    if (db.endsWith('_test') || isDev) delete dbActions[0]; // Test-DB
    if (!this.authservice.token.rights.dev || isDev) delete dbActions[1]; // Dev-DB
    if (!this.authservice.token.rights.dev) delete dbActions[2][0]; // ConStr

    const gleiseActions = this.actionsTree.find(group => group.groupTitle == 'Gleise').actions;
    const isBVBDb = db == 'mrpro_bvb_basel';
    if (!isBVBDb) {
      delete gleiseActions[2];
    }
  }

  ngOnDestroy(): void {
    this.spinner.disable();
    if (!this.webSocket.connected) return;

    this.webSocket.invokeHandler(
      'stopFunctions',
      null,
      () => this.webSocket.stopCon(),
      (err: any) => {
        if (!/connection.+?closed/.test((err as Error).message)) console.log(err);
      }
    );
  }
  async createTestDatabase() {
    this.doCreateTestDatabase = true;
    const sendObj = {
      emails: this.selectedUsers.map(user => user.UserMail),
    };

    await this.startHubConnection();
    this.action.set(this.devDatabase == 'Dev-Datenbank' ? 'createDevDatabase' : 'createTestDatabase');
    this.spinner.enable();
    try {
      const { success, error } = await firstValueFrom(
        this.apiService.SysManStartAction(this.action(), sendObj, this.webSocket._id())
      );

      if (success) this.toastr.success(this.mrTranslate.transform(this.devDatabase + ' wurde erfolgreich erstellt'));
      else this.text = error;

      this.doCreateTestDatabase = false;
      setTimeout(() => (this.showCreateTestDatabase = false), 1500);
    } catch (er) {
      const message = errorHandle(er);
      this.text = message;
      this.toastr.error(this.mrTranslate.transform('Etwas ist schief gelaufen'));
    } finally {
      this.pcnt = '';
      this.spinner.disable();
      this.webSocket.stopCon();
    }
  }

  async setView(action: ActionMode) {
    this.action.set(action);
    this.tblLeft.set(undefined);
    this.tblRight.set(undefined);

    this.navPanel().close();

    if (action == 'Auftragsgruppenberechtigungen' || action == 'Gruppenberechtigungen') return;

    if (action == 'createBackupDatabase') {
      /*
      const text = this.mrTranslate.transform(
        'Sind Sie sicher, dass Sie eine Sicherung der Datenbank erstellen möchten?'
      );
      if (await this.confirm.info(text)) 
      
      */
      this.createBackUpDB();
      return;
    }

    if (action == 'createTestDatabase' || action == 'createDevDatabase') {
      this.devDatabase = action == 'createDevDatabase' ? 'Dev-Datenbank' : 'Testdatenbank';

      this.users ??= await firstValueFrom(this.apiService.getUsers());
      this.selectedUsers = this.users.rows;
      this.text = 'Action: ';
      this.showCreateTestDatabase = true;

      return;
    }

    if (action == 'checkLogs') {
      this.logDates = [];
      for (let i = 0; i < 30; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setMonth(date.getMonth());
        date.setFullYear(date.getFullYear());
        this.logDates.push({ name: date.toLocaleDateString(), value: date.toISOString() });
      }

      return;
    }

    this.info = '';
    this.pcnt = '';
    this.bemerkung.set('');
    this.showdetailview = false;

    const res = await firstValueFrom(this.apiService.getSysManWindow(action));
    if (Array.isArray(res)) {
      //* xlSpez | QRCode | xlStammVorlageExp | xlStammEditExp | xlSpezEditExp
      this.sotypListe.set(res);
    } else {
      //* inspRestore | inspFullDelete | masterRestore | masterDelete | masterDeleteMultiple |
      //* masterArchive | masterCopy | masterUnlock | moveStart | lineCut | lineConnect | lineTurn
      this.tblLeft.set(res);
      if (action == 'lineConnect') this.tblRight.set(res);
    }
  }

  BackupFiles: any = [];
  selectedPaths: string[] = [];
  isBackupFinished: boolean = false;
  isBackupStarted: boolean = false;
  onCheckboxChange(path: string, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedPaths.push(path);
    } else {
      this.selectedPaths = this.selectedPaths.filter(p => p !== path);
    }
  }

  async createBackUpDB() {
    const res = await firstValueFrom(this.apiService.getsBackupFolderInfo());
    this.isBackupFinished = false;
    if (res) {
      this.isBackupStarted = true;
      this.BackupFiles = res['result'];
      this.isDBBackup.set(true);
    }
  }

  async runBackUpDB() {
    this.isLoading.set(true);
    this.isBackupStarted = false;
    this.progressGroup.set({ status: 'idle' });
    await this.startGroupedConnection();

    try {
      const promises = this.selectedPaths.map(path => this.createBackupForPath(path));

      await Promise.allSettled(promises);
    } finally {
      this.isLoading.set(false);
      this.isBackupFinished = true;
      this.taskEnder();
    }
  }
  private async createBackupForPath(path: string) {
    try {
      const result =
        path === 'Datenbank' ?
          await firstValueFrom(this.apiService.getDatabaseBackUp(this.webSocket._id()))
        : await firstValueFrom(this.apiService.createFilesBackUp(this.webSocket._id(), path));

      if (!result.fileid) {
        throw new Error('No file ID returned');
      }

      const token = localStorage.getItem('jwt');

      const url =
        `${environment.apiUrl}/files/downloadbackup` +
        `?fileid=${result.fileid}` +
        `&backupFileName=${encodeURIComponent(result.backupFileName)}` +
        `&jwt=${token}`;

      const anchor = document.createElement('a');
      anchor.download = result.backupFileName;
      anchor.href = url;
      anchor.target = '_blank';

      anchor.click();
      anchor.remove();
    } catch (err) {
      console.error(`Backup failed for ${path}:`, err);
    }
  }

  async createBackUpDB_old() {
    this.isLoading.set(true);
    this.isDBBackup.set(true);

    this.progressGroup.set({ status: 'idle' });
    await this.startGroupedConnection();
    const promises = [
      //* Medien erstellen
      firstValueFrom(this.apiService.createFilesBackUp(this.webSocket._id(), ''))
        .then(({ backupFileName, fileid }) => {
          // & herunterladen
          if (!fileid) return;
          const token = localStorage.getItem('jwt');
          const url = `${environment.apiUrl}/files/downloadbackup?fileid=${fileid}&backupFileName=${encodeURIComponent(backupFileName)}&jwt=${token}`;

          const anchor = document.createElement('a');
          anchor.download = backupFileName;
          anchor.href = url;
          anchor.target = 'blank';
          anchor.click();
          anchor.remove();
        })
        .catch(err => console.error('Media Backup:', err)),

      //* Datenbanksicherung erstellen
      firstValueFrom(this.apiService.getDatabaseBackUp(this.webSocket._id()))
        .then(({ backupFileName, fileid }) => {
          // & herunterladen
          const token = localStorage.getItem('jwt');
          const url = `${environment.apiUrl}/files/downloadbackup?fileid=${fileid}&backupFileName=${encodeURIComponent(backupFileName)}&jwt=${token}`;

          const anchor = document.createElement('a');
          anchor.download = backupFileName;
          anchor.href = url;
          anchor.target = 'blank';
          anchor.click();
          anchor.remove();
        })
        .catch(err => console.error('DB Backup:', err)),
    ];

    await Promise.allSettled(promises);

    this.isLoading.set(false);
    this.taskEnder();
  }

  async startAction(event?: unknown) {
    if (this.action() == 'QRCode') {
      return this.getQRCodes();
    }
    if (this.action() == 'deleteTestDatabase') {
      const text = this.mrTranslate.transform('Testdatenbank wirklich löschen?');
      const ok = await this.confirm.danger(text);
      if (!ok) return;
    }
    if (this.action() == 'deleteDevDatabase') {
      const text = this.mrTranslate.transform('Devdatenbank wirklich löschen?');
      const ok = await this.confirm.danger(text);
      if (!ok) return;
    }
    this.bemerkung.set(null);
    this.spinner.enable();
    try {
      const sendObj: Record<string, unknown> = {};
      await this.startHubConnection();
      switch (this.action()) {
        case 'inspRestore':
        case 'inspFullDelete':
          const OPRUEFID = this.selectedtabellenL().OPRUEFID as number;
          if (OPRUEFID <= 0) {
            this.toastr.warning('Dieses Stammdokument besitzt keine Prüfungsdaten.');
            return;
          }
          sendObj.pID = OPRUEFID;
          break;
        case 'masterRestore':
        case 'masterArchive':
          sendObj.sID = this.selectedtabellenL().OSTAMMID;
          break;
        case 'masterDelete':
        case 'masterDeleteMultiple':
        case 'masterUnlock':
          sendObj.IDs = this.selectedtabellenLM().map(row => +row.OSTAMMID);
          sendObj.deleteAll = this.sxmrepDelete;
          break;

        case 'masterCopy':
          sendObj.bez = this.copyBezeichnung().trim();
          sendObj.copyID = this.selectedtabellenL().OSTAMMID;
          break;
        case 'moveStart': {
          const { offset, toDrehen } = this.moveGleisForm.value;
          if (this.moveGleisForm.invalid) return;
          sendObj.sID = this.selectedtabellenL().OSTAMMID;
          sendObj.toDrehen = toDrehen;
          sendObj.offset = offset;
          break;
        }
        case 'lineConnect':
          sendObj.stammIDL = this.selectedtabellenL().OSTAMMID;
          sendObj.stammIDR = this.selectedtabellenR().OSTAMMID;
          if (sendObj.stammIDL == sendObj.stammIDR) {
            this.toastr.warning('Es dürfen nicht die gleichen Stammobjekte verbunden werden.');
            return;
          }
          break;
        case 'lineCut': {
          sendObj.sID = this.selectedtabellenL().OSTAMMID;

          const { cutL, cutR } = this.cutGleisForm.value;

          sendObj.OldEP = cutL.Endpunkt;
          sendObj.OldEM = cutL.Endposition;
          sendObj.OldL = cutL.Gleislaenge;
          sendObj.NewSP = cutR.Startpunkt;
          sendObj.NewEP = cutR.Endpunkt;
          sendObj.NewMF = cutR.Messfile;
          sendObj.NewBez = cutR.Bezeichnung;
          sendObj.NewSM = cutR.Startposition;
          sendObj.NewEM = cutR.Endposition;
          sendObj.NewL = cutR.Gleislaenge;

          break;
        }
        case 'lineTurn':
          sendObj.sID = this.selectedtabellenL().OSTAMMID;
          sendObj.offset = 0;
          break;
        case 'xlSpez':
          sendObj.OTYPIDs = this.sotypListe().flatMap(row => (row.selected == 1 ? [row.ID] : []));
          break;
        case 'xlStammVorlageExp':
        case 'xlStammEditExp':
        case 'xlSpezEditExp': {
          sendObj.id = (event as ObjektTypModel).ID;
          break;
        }
        case 'xlSpezEditImp':
        case 'xlStammVorlageImp':
        case 'xlStammEditImp': {
          if (!(event instanceof HTMLInputElement)) return;
          const table = await this.reader.getArrayBufferFromFile(event.files[0]);
          event.value = null;
          const workbook = new Workbook();
          await workbook.xlsx.load(table);
          const worksheet = workbook.worksheets[0];
          const rows: Array<CellValue[] | Record<string, CellValue>> = [];
          worksheet.eachRow({ includeEmpty: false }, row => rows.push(row.values));

          const { success, error } = await firstValueFrom(
            this.apiService.SysManStartAction(this.action(), { rows }, this.webSocket._id())
          );

          if (success) this.toastr.success(this.mrTranslate.transform('Aktion ausgeführt'));
          this.bemerkung.set(error ?? this.mrTranslate.transform('Erfolgreich importiert'));
          return;
        }
        default:
          break;
      }

      const { success, error, ...res } = await firstValueFrom(
        this.apiService.SysManStartAction(this.action(), sendObj, this.webSocket._id())
      );

      if (error == 'Canceled') {
        this.toastr.warning(this.mrTranslate.transform('Die Aktion wurde abgebrochen'));
        return;
      }

      if (!success) throw new Error(error);

      if ('warning' in res && res.warning) this.toastr.warning(res.warning as string);

      switch (this.action()) {
        case 'xlSpez': {
          if ('warning' in res && res.warning) return;
          const { url, filename } = await createExcel_SpezifikationListe(
            this.sotypListe().filter(typ => typ.selected),
            res
          );
          this.downloadExcel(url, filename);
          break;
        }
        case 'xlBauw': {
          if (!('dt' in res) || typeof res.dt != 'object') return;
          this.SetUpExcelDoc(res?.dt, 'Bauwerke');
          break;
        }
        case 'xlHerst': {
          if (!('dt' in res) || typeof res.dt != 'object') return;
          this.SetUpExcelDoc(res?.dt, 'Hersteller');
          break;
        }
        case 'xlStammVorlageExp':
        case 'xlStammEditExp': {
          if ('warning' in res && res.warning) return;
          const { url, filename } = await exportTableInExcel(
            event,
            res,
            'Stammdaten',
            this.mrTranslate,
            this.localeDate
          );
          this.downloadExcel(url, filename);
          break;
        }
        case 'xlSpezEditExp': {
          if ('warning' in res && res.warning) return;
          const { url, filename } = await exportTableInExcel(
            event,
            res,
            'Spezifikation',
            this.mrTranslate,
            this.localeDate
          );
          this.downloadExcel(url, filename);
          break;
        }
        case 'refreshDBConDictonary':
        case 'redoCheckDatabase': {
          this.toastr.success(this.mrTranslate.transform('Aktion ausgeführt'));
          return;
        }
        case 'assignInsp':
        case 'assignMess':
        case 'assignError':
        case 'sortOrder':
        case 'kav_calculate': {
          this.bemerkung.set('info' in res && Array.isArray(res.info) ? res?.info?.join('\n') : null);
          this.info = '';
          this.pcnt = '';
          this.toastr.success(this.mrTranslate.transform('Aktion ausgeführt'));
          return;
        }
      }
      this.tblLeft.set(undefined);
      this.tblRight.set(undefined);
      this.selectedtabellenL.set(null);
      this.selectedtabellenR.set(null);
      this.selectedtabellenLM.set([]);
      this.setView(this.action());
      this.info = '';
      this.toastr.success(this.mrTranslate.transform('Aktion ausgeführt'));
    } catch (er) {
      const message = errorHandle(er);
      this.info = message;
      this.toastr.error(message, this.mrTranslate.transform('Etwas ist schief gelaufen'));
    } finally {
      this.info = '';
      this.pcnt = '';
      this.spinner.disable();
      this.webSocket.stopCon();
    }
  }

  private downloadExcel(url: string, filename: string) {
    const anchor = this.doc.createElement('a');
    anchor.download = filename;
    anchor.href = url;
    anchor.click();
    anchor.remove();
  }

  stopAction() {
    this.ngOnDestroy();
  }

  private async getQRCodes() {
    try {
      this.spinner.enable();

      const sendObj: Record<number, string> = {};
      for (const row of this.sotypListe()) {
        if (!row.selected) continue;
        sendObj[row.ID] = row.BEZEICHNUNG;
      }
      const res = await firstValueFrom(this.apiService.getQRLables(sendObj));
      const blob = new Blob([res], { type: 'application/pdf' });
      const anchor = document.createElement('a');
      anchor.download = 'qr-codes.pdf';
      anchor.href = (window.webkitURL || window.URL).createObjectURL(blob);
      anchor.click();
    } catch (er) {
      const message = errorHandle(er);
      console.log(message);
      this.toastr.warning(this.mrTranslate.transform('Kein PDF erstellt, Fehler im Log.'));
    } finally {
      this.spinner.disable();
    }
  }

  private SetUpExcelDoc(res: any, name: string) {
    const wb = new Workbook();
    this.rN = 1;
    const ws = wb.addWorksheet(name);
    this.ExcelCreateList(res, ws);
    this.adjustExcelColumnWidth(ws);
    wb.xlsx.writeBuffer().then(buffer => {
      this.apiService.downloadBlobAsFile(buffer, name + '.xlsx');
    });
  }

  private ExcelCreateList(obj: any, ws: Worksheet, rN?: number): number {
    rN ??= this.rN;
    for (let i = 0; i < obj.columns.length; i++) {
      ws.getRow(rN).getCell(i + 1).value = obj.columns[i].id;
    }
    ws.getRow(rN).eachCell({ includeEmpty: false }, function (cell) {
      ws.getCell(cell.address).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'BFBFBF' },
      };
      ws.getCell(cell.address).border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
    rN += 1;
    for (let i = 0; i < obj.rows.length; i++) {
      for (let j = 0; j < obj.columns.length; j++) {
        ws.getRow(i + rN).getCell(j + 1).value = obj.rows[i][obj.columns[j].id];
      }
      ws.getCell(i + rN, 1).border = { left: { style: 'thin' } };
      ws.getCell(i + rN, obj.columns.length).border = { right: { style: 'thin' } };
    }
    rN += obj.rows.length;
    ws.getRow(rN - 1).eachCell({ includeEmpty: true }, cell => {
      ws.getCell(cell.address).border = { bottom: { style: 'thin' } };
    });
    ws.getCell(rN - 1, obj.columns.length).border = { right: { style: 'thin' }, bottom: { style: 'thin' } };
    rN += 4;

    this.rN = rN;
    return rN;
  }

  private adjustExcelColumnWidth(worksheet: Worksheet) {
    worksheet.columns.forEach(column => {
      const lengths = column.values.map(v => v.toString().length);
      const maxLength = Math.max(...lengths.filter(v => typeof v === 'number'));
      column.width = maxLength > 10 ? maxLength : 10;
    });
  }

  async startGroupedConnection() {
    this.webSocket._config.name = `BackUp`;
    await this.webSocket.startCon();
    this.webSocket.registerHandler('taskStarted', this.taskStarter);
    this.webSocket.registerHandler('taskProgressChanged', this.taskProgressChanger);
    this.webSocket.registerHandler('taskErrored', this.taskErrorer);
    this.webSocket.registerHandler('taskEnded', this.taskEnder);
  }

  private taskStarter = ({ id, title }: ProgressEvent) => {
    this.progressGroup.update(group => {
      const initTask: ProgressStatus = { title, percent: 0 };
      if (group.status == 'idle' || group.status == 'done') return { status: 'pending', children: { [id]: initTask } };
      else {
        group.children[id] = initTask;
        return { ...group };
      }
    });
  };

  private taskProgressChanger = ({ id, percent, current }: ProgressEvent) => {
    this.progressGroup.update(group => {
      if (group.status != 'idle') {
        let child = group.children[id];
        child = { ...child, percent, current };
        group.children = { ...group.children, [id]: child };
      }
      return { ...group };
    });
  };

  private taskEnder = ({ id, message }: ProgressEvent = {}) => {
    const group = this.progressGroup();
    if (group.status == 'idle') return;

    this.progressGroup.update(group => {
      if (group.status != 'idle') {
        if (!id) {
          //for (const id in group.children) {
          //  if (!group.children[id].error) delete group.children[id];
          //}

          return { ...group, status: 'done' };
        }

        if (message) {
          group.children[id].info = message;
          return { ...group };
        }

        //delete group.children[id];
        if (!Object.keys(group.children).length) {
          return { status: 'done', children: null };
        }
      }
      return { ...group };
    });
  };

  private taskErrorer = ({ id, message }: ProgressEvent) => {
    console.error(message);
    this.progressGroup.update(group => {
      if (group.status != 'idle') {
        group.children[id].error = message;
        return { ...group, status: 'error' };
      }
      return { ...group };
    });
  };

  async startHubConnection() {
    await this.webSocket.startCon();
    this.taskRun = true;
    this.webSocket.onClosed(() => (this.taskRun = false));
    this.webSocket.registerHandler('taskProgressChanged', ([info, percent]) => {
      this.pcnt = percent;
      this.info = `Fortschritt: ${info}`;
      if (this.showCreateTestDatabase) this.text = `Action: ${info}`;
    });
  }

  changeLogDate(event: any) {
    if (this.pw()) {
      let pwhash = shajs('sha256').update(this.pw()).digest('hex');
      this.apiService
        .getLogEntry(event, pwhash)
        .pipe()
        .subscribe((res: any) => {
          this.bemerkung.set(res);
        });
    }
  }

  enlargeSidenav(event) {
    if (event.mode == 'full') {
      this.sidenavenlarged = event.val;
    } else if (event.mode == 'half') {
      this.split50 = event.val;
    } else if (event.mode == 'hidden') {
      this.hidedetailview = event.val;
    } else if (event.mode == 'close') {
      this._ostammid = undefined;
      setTimeout(() => {
        this.showdetailview = false;
      });
    }
  }

  close() {
    this.closedialog.next(true);
  }

  //#region BVB Import Spezifikation für Gleis
  @ViewChild('fileInputSHP')
  fileInput!: ElementRef<HTMLInputElement>;

  currentType = signal('');
  selSchienenformFile = signal<File | null>(null);
  selBefestigungFile = signal<File | null>(null);
  selUnterbauFile = signal<File | null>(null);
  selEindeckungFile = signal<File | null>(null);
  selEinbaujahrGleisFile = signal<File | null>(null);
  selEinbaujahrUnterbauFile = signal<File | null>(null);
  selDaemmungFile = signal<File | null>(null);
  selSchienengueteFile = signal<File | null>(null);

  selSchienenformFileName = signal('');
  selBefestigungFileName = signal('');
  selUnterbauFileName = signal('');
  selDaemmungFileName = signal('');
  selSchienengueteFileName = signal('');
  selEindeckungFileName = signal('');
  selEinbaujahrGleisFileName = signal('');
  selEinbaujahrUnterbauFileName = signal('');

  selRadiusFileName = signal('');
  selRadiusFile = signal<File | null>(null);
  dbfFieldsRadius = signal<Array<{ name: string; type: string; length: number; decimals: number }>>([]);
  tableRadius = signal<TableModel | null>(null);
  selRadiusReferenz = model('');
  hasSelectionRadiusReferenz = computed(() => !!this.selRadiusReferenz());

  dbfFieldsSchiene = signal<Array<{ name: string; type: string; length: number; decimals: number }>>([]);
  dbfFieldsBefestigung = signal<Array<{ name: string; type: string; length: number; decimals: number }>>([]);
  dbfFieldsUnterbau = signal<Array<{ name: string; type: string; length: number; decimals: number }>>([]);
  dbfFieldsEindeckung = signal<Array<{ name: string; type: string; length: number; decimals: number }>>([]);
  dbfFieldsEinbaujahrGleis = signal<Array<{ name: string; type: string; length: number; decimals: number }>>([]);
  dbfFieldsEinbaujahrUnterbau = signal<Array<{ name: string; type: string; length: number; decimals: number }>>([]);
  dbfFieldsDaemmung = signal<Array<{ name: string; type: string; length: number; decimals: number }>>([]);
  dbfFieldsSchienenguete = signal<Array<{ name: string; type: string; length: number; decimals: number }>>([]);

  tableSchiene = signal<TableModel | null>(null);
  tableBefestigung = signal<TableModel | null>(null);
  tableUnterbau = signal<TableModel | null>(null);
  tableEindeckung = signal<TableModel | null>(null);
  tableEinbaujahrGleis = signal<TableModel | null>(null);
  tableEinbaujahrUnterbau = signal<TableModel | null>(null);
  tableDaemmung = signal<TableModel | null>(null);
  tableSchienenguete = signal<TableModel | null>(null);

  curtable = signal<TableModel | null>(null);

  schiene: Interval<any>[] = [];
  befestigung: Interval<any>[] = [];
  unterbau: Interval<any>[] = [];
  eindeckung: Interval<any>[] = [];
  einbaujahr: Interval<any>[] = [];
  einbaujahr_unterbau: Interval<any>[] = [];
  daemmung: Interval<any>[] = [];
  schienenguete: Interval<any>[] = [];

  dbfMappings: Record<string, DbfMapping> = {
    schienenform: {
      referenzZumGleis: '',
      bezeichnung: '',
      startmeter: '',
      endmeter: '',
    },
    befestigung: {
      referenzZumGleis: '',
      bezeichnung: '',
      startmeter: '',
      endmeter: '',
    },
    unterbau: {
      referenzZumGleis: '',
      bezeichnung: '',
      startmeter: '',
      endmeter: '',
    },
    eindeckung: {
      referenzZumGleis: '',
      bezeichnung: '',
      startmeter: '',
      endmeter: '',
    },
    einbaujahrgleis: {
      referenzZumGleis: '',
      bezeichnung: '',
      startmeter: '',
      endmeter: '',
    },
    einbaujahrunterbau: {
      referenzZumGleis: '',
      bezeichnung: '',
      startmeter: '',
      endmeter: '',
    },
    daemmung: {
      referenzZumGleis: '',
      bezeichnung: '',
      startmeter: '',
      endmeter: '',
    },
    schienenguete: {
      referenzZumGleis: '',
      bezeichnung: '',
      startmeter: '',
      endmeter: '',
    },
  };

  mappingErrors: Record<string, string[]> = {};
  isMappingValid = signal(false);
  showModalTable = false;
  showModalTitle = signal('');

  validateMappings(): void {
    this.mappingErrors = {};
    this.isMappingValid.set(true);

    for (const [key, mapping] of Object.entries(this.dbfMappings)) {
      const values = Object.values(mapping);
      const errors: string[] = [];

      // empty fields
      if (values.some(v => !v?.trim())) {
        errors.push('Alle Felder müssen ausgewählt werden.');
      }

      // duplicate values within one mapping
      if (new Set(values).size !== values.length) {
        errors.push('Jedes Feld muss unterschiedlich sein.');
      }

      if (errors.length > 0) {
        this.mappingErrors[key] = errors;
        this.isMappingValid.set(false);
      }
    }
  }

  openFileDialog(type: string) {
    this.currentType.set(type);
    this.fileInput.nativeElement.click();
  }

  showInfoTable(type: string) {
    switch (type) {
      case 'radius':
        this.curtable.set(this.tableRadius());
        break;

      case 'schienenform':
        this.curtable.set(this.tableSchiene());
        break;
      case 'befestigung':
        this.curtable.set(this.tableBefestigung());
        break;
      case 'unterbau':
        this.curtable.set(this.tableUnterbau());
        break;
      case 'eindeckung':
        this.curtable.set(this.tableEindeckung());
        break;
      case 'einbaujahrgleis':
        this.curtable.set(this.tableEinbaujahrGleis());
        break;
      case 'einbaujahrunterbau':
        this.curtable.set(this.tableEinbaujahrUnterbau());
        break;
      case 'daemmung':
        this.curtable.set(this.tableDaemmung());
        break;
      case 'schienenguete':
        this.curtable.set(this.tableSchienenguete());
        break;
    }

    this.showModalTable = true;
    this.showModalTitle.set(`Tabelle: ${type.toUpperCase()}`);
  }

  async onShpFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (!file) {
      return;
    }

    const buffer = await file.arrayBuffer();
    const fields = this.readDbfHeader(buffer);
    const table = this.toTable(this.readDbfRecords(buffer));

    switch (this.currentType()) {
      case 'radius':
        this.selRadiusFile.set(file);
        this.selRadiusFileName.set(file.name);
        this.dbfFieldsRadius.set(fields);
        this.tableRadius.set(table);
        break;
      case 'schienenform':
        this.selSchienenformFile.set(file);
        this.selSchienenformFileName.set(file.name);
        this.dbfFieldsSchiene.set(fields);
        this.tableSchiene.set(table);
        break;

      case 'befestigung':
        this.selBefestigungFile.set(file);
        this.selBefestigungFileName.set(file.name);
        this.dbfFieldsBefestigung.set(fields);
        this.tableBefestigung.set(table);
        break;

      case 'unterbau':
        this.selUnterbauFile.set(file);
        this.selUnterbauFileName.set(file.name);
        this.dbfFieldsUnterbau.set(fields);
        this.tableUnterbau.set(table);
        break;

      case 'eindeckung':
        this.selEindeckungFile.set(file);
        this.selEindeckungFileName.set(file.name);
        this.dbfFieldsEindeckung.set(fields);
        this.tableEindeckung.set(table);
        break;

      case 'einbaujahrgleis':
        this.selEinbaujahrGleisFile.set(file);
        this.selEinbaujahrGleisFileName.set(file.name);
        this.dbfFieldsEinbaujahrGleis.set(fields);
        this.tableEinbaujahrGleis.set(table);
        break;

      case 'einbaujahrunterbau':
        this.selEinbaujahrUnterbauFile.set(file);
        this.selEinbaujahrUnterbauFileName.set(file.name);
        this.dbfFieldsEinbaujahrUnterbau.set(fields);
        this.tableEinbaujahrUnterbau.set(table);
        break;

      case 'daemmung':
        this.selDaemmungFile.set(file);
        this.selDaemmungFileName.set(file.name);
        this.dbfFieldsDaemmung.set(fields);
        this.tableDaemmung.set(table);
        break;

      case 'schienenguete':
        this.selSchienengueteFile.set(file);
        this.selSchienengueteFileName.set(file.name);
        this.dbfFieldsSchienenguete.set(fields);
        this.tableSchienenguete.set(table);
        break;

      default:
        console.warn(`Unbekannter Dateityp: ${this.currentType}`);
        break;
    }

    (event.target as HTMLInputElement).value = '';
  }

  readDbfRecords(buffer: ArrayBuffer) {
    const view = new DataView(buffer);

    const recordCount = view.getUint32(4, true);
    const headerLength = view.getUint16(8, true);
    const recordLength = view.getUint16(10, true);

    const fields = this.readDbfHeader(buffer);

    const records = [];

    for (let i = 0; i < recordCount; i++) {
      const offset = headerLength + i * recordLength;

      // Skip deleted records
      if (String.fromCharCode(view.getUint8(offset)) === '*') {
        continue;
      }

      let fieldOffset = offset + 1;
      const record: any = {};

      for (const field of fields) {
        const bytes = new Uint8Array(buffer, fieldOffset, field.length);

        const value = new TextDecoder().decode(bytes).trim();

        record[field.name] = value;

        fieldOffset += field.length;
      }

      records.push(record);
    }

    return records;
  }

  toTable(records: any[]): TableModel {
    const columns = records.length > 0 ? Object.keys(records[0]) : [];

    return {
      columns,
      rows: records,
    };
  }

  readDbfHeader(buffer: ArrayBuffer) {
    const view = new DataView(buffer);

    const headerLength = view.getUint16(8, true);

    const fields = [];
    let offset = 32;

    while (offset < headerLength - 1) {
      const nameBytes = new Uint8Array(buffer, offset, 11);

      const name = new TextDecoder().decode(nameBytes).replace(/\0/g, '').trim();

      const type = String.fromCharCode(view.getUint8(offset + 11));
      const length = view.getUint8(offset + 16);
      const decimals = view.getUint8(offset + 17);

      fields.push({
        name,
        type,
        length,
        decimals,
      });

      offset += 32;
    }

    return fields;
  }

  getImportBVBStammden() {
    let s = localStorage.getItem('gis');
    if (s != null) {
      this.dbfMappings = JSON.parse(s);
      this.validateMappings();
    }
  }

  saveImportBVBStammden() {
    localStorage.setItem('gis', JSON.stringify(this.dbfMappings));
  }

  mergeRadiusTabelle(items: ErgebnisRadius[]): ErgebnisRadius[] {
    if (items.length === 0) {
      return [];
    }

    items.sort((a, b) => {
      if (a.OSTAMMID !== b.OSTAMMID) {
        return a.OSTAMMID - b.OSTAMMID;
      }
      return a.Anfang - b.Anfang;
    });

    const result: ErgebnisRadius[] = [];

    let current = { ...items[0] };

    for (let i = 1; i < items.length; i++) {
      const next = items[i];

      const gleicherAbschnitt =
        current.OSTAMMID === next.OSTAMMID &&
        current.RadiusWert1 === next.RadiusWert1 &&
        current.RadiusWert2 === next.RadiusWert2;

      const direktAnschließend = Math.abs(current.Ende - next.Anfang) < 0.01;

      if (gleicherAbschnitt && direktAnschließend) {
        current.Ende = next.Ende;
      } else {
        result.push(current);
        current = { ...next };
      }
    }

    result.push(current);

    return result;
  }

  getTrassierungen(): Trassierung[] {
    const table = this.tableRadius();

    if (!table) {
      return [];
    }

    return table.rows
      .map(row => ({
        ID: Number(row.id),
        GISID: Number(row[this.selRadiusReferenz()]),
        Länge: Number(row.st_length),
        Radius: row.bvb910_rad === '***********' ? 0 : Number(row.bvb910_rad),
        art: row.z_bvb910a_ as 'Linie' | 'Übergangsbogen' | 'Bogen',
      }))
      .sort((a, b) => a.ID - b.ID);
  }

  async startImportBVBRadius() {
    this.spinner.enable();
    let tracksegments: any = [];

    this.apiService.getGisMappingBVB().subscribe((res: any) => {
      let lrsTable = res.lrs.rows;
      let gleistable = res.stamm.rows;

      let trassierungen: Trassierung[] = this.getTrassierungen();

      const RadiusTabelle: ErgebnisRadius[] = [];
      const gruppen = new Map<number, Trassierung[]>();

      for (const t of trassierungen) {
        if (!gruppen.has(t.GISID)) {
          gruppen.set(t.GISID, []);
        }
        gruppen.get(t.GISID)!.push(t);
      }

      for (const obj of gleistable) {
        if (obj.lrs_track == null) {
          continue;
        }

        const liste = (gruppen.get(Number(obj.lrs_track)) ?? []).sort((a, b) => a.ID - b.ID);

        // absolute Trassierung erzeugen
        const abschnitte: AbschnittRadius[] = [];
        let station = 0;
        let letzterRadius = 0;

        for (let i = 0; i < liste.length; i++) {
          const t = liste[i];

          const start = station;
          const ende = start + t.Länge;

          let r1 = 0;
          let r2 = 0;

          switch (t.art) {
            case 'Linie':
              r1 = 0;
              r2 = 0;
              letzterRadius = 0;
              break;

            case 'Bogen':
              r1 = t.Radius ?? 0;
              r2 = t.Radius ?? 0;
              letzterRadius = r1;
              break;

            case 'Übergangsbogen':
              r1 = letzterRadius;

              // nächsten echten Radius suchen
              let nextRadius = 0;

              for (let j = i + 1; j < liste.length; j++) {
                if (liste[j].art === 'Bogen') {
                  nextRadius = liste[j].Radius ?? 0;
                  break;
                }
                if (liste[j].art === 'Linie') {
                  nextRadius = 0;
                  break;
                }
              }

              r2 = nextRadius;
              break;
          }

          abschnitte.push({
            start: Number(start.toFixed(2)),
            ende: Number(ende.toFixed(2)),
            radius1: Number(r1.toFixed(2)),
            radius2: Number(r2.toFixed(2)),
          });

          station = ende;
        }

        // Objekt mit Trassierung verschneiden
        for (const a of abschnitte) {
          const start = Math.max(obj.startmeter, a.start);
          const ende = Math.min(obj.endmeter, a.ende);

          if (start < ende) {
            RadiusTabelle.push({
              OSTAMMID: obj.OSTAMMID,
              Anfang: start,
              Ende: ende,
              RadiusWert1: a.radius1,
              RadiusWert2: a.radius2,
            });
          }
        }
      }

      if (RadiusTabelle.length > 0) {
        const radiusTabelleMerged = this.mergeRadiusTabelle(RadiusTabelle);

        this.apiService.ImportBVBRadius(radiusTabelleMerged).subscribe((res: any) => {
          if (res.success == true) {
            this.toastr.success(this.mrTranslate.transform('Trassierung wurde erfolgreich importiert'));
          } else {
            this.toastr.error(res.error, this.mrTranslate.transform('Es ist ein fehler aufgetretet'));
          }
          this.spinner.disable();
        });
      }
    });
  }

  async startImportBVBStammden() {
    this.spinner.enable();
    localStorage.setItem('gis', JSON.stringify(this.dbfMappings));
    let tracksegments: any = [];
    this.apiService.getGisMappingBVB().subscribe((res: any) => {
      let lrsTable = res.lrs.rows;
      let gleistable = res.stamm.rows;

      lrsTable.forEach((row: any) => {
        const lrs = row['lrs_track'];
        const start = row['startmeter'];
        const end = row['endmeter'];

        this.schiene = this.tableSchiene()
          .rows.filter(row => row[this.dbfMappings.schienenform.referenzZumGleis] === lrs)
          .map(row => ({
            from: Number(Number(row[this.dbfMappings.schienenform.startmeter]).toFixed(2)),
            to: Number(Number(row[this.dbfMappings.schienenform.endmeter]).toFixed(2)),
            value: row[this.dbfMappings.schienenform.bezeichnung],
          }));

        this.befestigung = this.tableBefestigung()
          .rows.filter(row => row[this.dbfMappings.befestigung.referenzZumGleis] === lrs)
          .map(row => ({
            from: Number(Number(row[this.dbfMappings.befestigung.startmeter]).toFixed(2)),
            to: Number(Number(row[this.dbfMappings.befestigung.endmeter]).toFixed(2)),
            value: row[this.dbfMappings.befestigung.bezeichnung],
          }));

        this.unterbau = this.tableUnterbau()
          .rows.filter(row => row[this.dbfMappings.unterbau.referenzZumGleis] === lrs)
          .map(row => ({
            from: Number(Number(row[this.dbfMappings.unterbau.startmeter]).toFixed(2)),
            to: Number(Number(row[this.dbfMappings.unterbau.endmeter]).toFixed(2)),
            value: row[this.dbfMappings.unterbau.bezeichnung],
          }));

        this.eindeckung = this.tableEindeckung()
          .rows.filter(row => row[this.dbfMappings.eindeckung.referenzZumGleis] === lrs)
          .map(row => ({
            from: Number(Number(row[this.dbfMappings.eindeckung.startmeter]).toFixed(2)),
            to: Number(Number(row[this.dbfMappings.eindeckung.endmeter]).toFixed(2)),
            value: row[this.dbfMappings.eindeckung.bezeichnung],
          }));

        this.einbaujahr = this.tableEinbaujahrGleis()
          .rows.filter(row => row[this.dbfMappings.einbaujahrgleis.referenzZumGleis] === lrs)
          .map(row => ({
            from: Number(Number(row[this.dbfMappings.einbaujahrgleis.startmeter]).toFixed(2)),
            to: Number(Number(row[this.dbfMappings.einbaujahrgleis.endmeter]).toFixed(2)),
            value: row[this.dbfMappings.einbaujahrgleis.bezeichnung],
          }));

        this.einbaujahr_unterbau = this.tableEinbaujahrUnterbau()
          .rows.filter(row => row[this.dbfMappings.einbaujahrunterbau.referenzZumGleis] === lrs)
          .map(row => ({
            from: Number(Number(row[this.dbfMappings.einbaujahrunterbau.startmeter]).toFixed(2)),
            to: Number(Number(row[this.dbfMappings.einbaujahrunterbau.endmeter]).toFixed(2)),
            value: row[this.dbfMappings.einbaujahrunterbau.bezeichnung],
          }));

        this.daemmung = this.tableDaemmung()
          .rows.filter(row => row[this.dbfMappings.daemmung.referenzZumGleis] === lrs)
          .map(row => ({
            from: Number(Number(row[this.dbfMappings.daemmung.startmeter]).toFixed(2)),
            to: Number(Number(row[this.dbfMappings.daemmung.endmeter]).toFixed(2)),
            value: row[this.dbfMappings.daemmung.bezeichnung],
          }));

        this.schienenguete = this.tableSchienenguete()
          .rows.filter(row => row[this.dbfMappings.schienenguete.referenzZumGleis] === lrs)
          .map(row => ({
            from: Number(Number(row[this.dbfMappings.schienenguete.startmeter]).toFixed(2)),
            to: Number(Number(row[this.dbfMappings.schienenguete.endmeter]).toFixed(2)),
            value: row[this.dbfMappings.schienenguete.bezeichnung],
          }));

        let segments = this.normalizeSegments(start, end, this.buildTrack());

        tracksegments.push({ LRS: lrs, segments: segments });
      });

      const GleisBauformen: GleisBauform[] = [];

      if (tracksegments.length > 0) {
        const lrsMap = new Map<number, TrackSegment[]>();

        tracksegments.forEach((t: any) => {
          lrsMap.set(t.LRS, t.segments);
        });

        gleistable.forEach((row: any) => {
          let _lrs = row['lrs_track'];

          const segments = lrsMap.get(row['lrs_track']) ?? [];

          for (const seg of segments) {
            const start = Math.max(seg.start, row['startmeter']);
            const end = Math.min(seg.end, row['endmeter']);

            if (start >= end) continue;

            let code =
              (seg.schiene ?? '?') +
              '-' +
              (seg.unterbau?.substring(1) ?? '') +
              '-' +
              (seg.befestigung?.substring(3) ?? '') +
              '-' +
              (seg.eindeckung?.substring(1) ?? '');

            const table: SpezPointType[] = [
              { orgName: 'strValue01', bezeichnung: code, objekt: 'Code' },
              { orgName: 'strValue02', bezeichnung: seg.schiene ?? '?', objekt: 'Schiene' },
              { orgName: 'strValue04', bezeichnung: seg.befestigung, objekt: 'Schienenbefestigung' },
              { orgName: 'strValue03', bezeichnung: seg.unterbau, objekt: 'Schwellenart' },
              { orgName: 'strValue05', bezeichnung: seg.eindeckung, objekt: 'Eindeckung' },
              { orgName: 'strValue06', bezeichnung: seg.einbaujahr, objekt: 'Baujahr' },
              { orgName: 'strValue07', bezeichnung: seg.einbaujahr_unterbau, objekt: 'Schwellenjahr' },
              { orgName: 'strValue08', bezeichnung: seg.schienenguete, objekt: 'Schienengüte' },
              { orgName: 'strValue26', bezeichnung: seg.daemmung, objekt: 'Dämmung' },
              { orgName: 's_StartMeter', bezeichnung: start, objekt: 'StartMeter' },
              { orgName: 's_EndMeter', bezeichnung: end, objekt: 'EndMeter' },
            ];
            GleisBauformen.push({
              ostammid: row['OSTAMMID'],
              bauform: {
                table,
                otypid: 2,
                typ: 4,
                smartInsert: false,
              },
            });
          }
        });
      }

      if (GleisBauformen.length > 0) {
        this.apiService.ImportBVBSpezifikationen(GleisBauformen).subscribe((res: any) => {
          if (res.success == true) {
            this.toastr.success(this.mrTranslate.transform('Bauform wurde erfolgreich importiert'));
          } else {
            this.toastr.error(res.error, this.mrTranslate.transform('Es ist ein fehler aufgetretet'));
          }
          this.spinner.disable();
        });
      }
    });
  }

  normalizeSegments(gleisStart: number, gleisEnd: number, segments: TrackSegment[]): TrackSegment[] {
    if (segments.length === 0) {
      return segments;
    }

    // Start korrigieren
    if (Math.abs(segments[0].start - gleisStart) < 0.05) {
      segments[0].start = gleisStart;
    }

    if (segments.length > 1) {
      // Vorletztes Segemnt korrigieren
      const last = segments[segments.length - 1];
      const previous = segments[segments.length - 2];
      const difference = last.end - previous.end;
      if (difference <= 0.02) {
        previous.end = last.end;
        segments.pop();
      }
    }

    // Ende korrigieren
    const lastSegment = segments[segments.length - 1];
    if (Math.abs(lastSegment.end - gleisEnd) < 0.05) {
      lastSegment.end = gleisEnd;
    }

    let changed = true;

    while (changed) {
      changed = false;

      for (let i = 0; i < segments.length; i++) {
        const current = segments[i];
        const length = current.end - current.start;

        // nur Mini-Segmente betrachten
        if (length >= 0.1) {
          continue;
        }

        const previous = i > 0 ? segments[i - 1] : undefined;
        const next = i < segments.length - 1 ? segments[i + 1] : undefined;

        // mit Vorgänger verschmelzen
        if (previous && this.sameProperties(previous, current)) {
          previous.end = current.end;
          segments.splice(i, 1);

          changed = true;
          break;
        }

        // mit Nachfolger verschmelzen
        if (next && this.sameProperties(next, current)) {
          next.start = current.start;
          segments.splice(i, 1);

          changed = true;
          break;
        }
      }
    }

    return segments;
  }

  sameProperties(a: TrackSegment, b: TrackSegment): boolean {
    return (
      a.schiene === b.schiene &&
      a.befestigung === b.befestigung &&
      a.unterbau === b.unterbau &&
      a.eindeckung === b.eindeckung &&
      a.einbaujahr === b.einbaujahr &&
      a.einbaujahr_unterbau === b.einbaujahr_unterbau &&
      a.daemmung === b.daemmung &&
      a.schienenguete === b.schienenguete
    );
  }

  buildTrack(): TrackSegment[] {
    const points = this.collectAllBreakpoints();
    const segments: TrackSegment[] = [];

    for (let i = 0; i < points.length - 1; i++) {
      const start = points[i];
      const end = points[i + 1];
      const mid = (start + end) / 2;

      segments.push({
        start,
        end,
        schiene: this.lookup(mid, this.schiene)!,
        befestigung: this.lookup(mid, this.befestigung),
        unterbau: this.lookup(mid, this.unterbau),
        eindeckung: this.lookup(mid, this.eindeckung),
        einbaujahr: this.lookup(mid, this.einbaujahr),
        einbaujahr_unterbau: this.lookup(mid, this.einbaujahr_unterbau),
        daemmung: this.lookup(mid, this.daemmung),
        schienenguete: this.lookup(mid, this.schienenguete),
      });
    }

    return segments;
  }

  collectAllBreakpoints(): number[] {
    const set = new Set<number>();

    const add = (arr: Interval<any>[]) => {
      arr.forEach(x => {
        set.add(x.from);
        set.add(x.to);
      });
    };

    add(this.schiene);
    add(this.befestigung);
    add(this.unterbau);
    add(this.eindeckung);
    add(this.einbaujahr);
    add(this.einbaujahr_unterbau);
    add(this.daemmung);
    add(this.schienenguete);

    return Array.from(set).sort((a, b) => a - b);
  }

  lookup<T>(x: number, data: Interval<T>[]): T | undefined {
    const tolerance = 0.001;
    return data.find(d => x >= d.from - tolerance && x < d.to + tolerance)?.value;
  }

  //# Endregion
}

type Interval<T> = {
  from: number;
  to: number;
  value: T;
};
interface GleisBauform {
  ostammid: number;
  bauform: Request;
}
interface Request {
  table: SpezPointType[];
  otypid: 2;
  typ: 4;
  smartInsert: false;
}
interface TrackSegment {
  start: number;
  end: number;
  schiene: string;
  befestigung?: string;
  unterbau?: string;
  eindeckung?: string;
  einbaujahr?: number;
  einbaujahr_unterbau?: number;
  daemmung?: string;
  schienenguete?: string;
}

interface TableModel {
  columns: string[];
  rows: any[];
}

export interface DbfMapping {
  referenzZumGleis: string;
  bezeichnung: string;
  startmeter: string;
  endmeter: string;
}

interface CutGleisModel {
  Startpunkt: string;
  Endpunkt: string;
  Bezeichnung: string;
  Startposition: number;
  Endposition: number;
  Messfile: string;
  Gleislaenge: number;
}

export interface ObjektTypModel {
  selected: ClrSelectedState;
  ID: number;
  BEZEICHNUNG: string;
  NAME: string;
  ANZAHL: number;
  SysIcon: number;
}

interface ErgebnisRadius {
  OSTAMMID: number;
  Anfang: number;
  Ende: number;
  RadiusWert1: number;
  RadiusWert2: number;
}

interface AbschnittRadius {
  start: number;
  ende: number;
  radius1: number;
  radius2: number;
}

interface Trassierung {
  ID: number;
  GISID: number;
  Länge: number;
  Radius: number;
  art: 'Linie' | 'Übergangsbogen' | 'Bogen';
}
